import { useQueryClient } from '@tanstack/react-query';
import update from 'immutability-helper';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProps,
} from 'react-beautiful-dnd';
import styled from 'styled-components';

import { Table, TableContent, Th } from '@/components/Table';
import useToasts from '@/hooks/useToasts';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';

import SortableTableRow from './SortableTableRow';

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 24px;
    font-size: 11px;
    vertical-align: top;

    &:first-child {
      width: 10%;
    }
    &:nth-child(2) {
      width: 15%;
    }
    &:nth-child(3) {
      width: 25%;
    }
    &:nth-child(4) {
      width: 50%;
    }
  }
`;

const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

interface Props {
  posts: EditorialFacebookPostResource[];
}

const SortableTable: FC<Props> = ({ posts }) => {
  const [sortablePosts, setSortablePosts] = useState(
    posts.map((p) => ({ ...p, id: p.id.toString() })),
  );

  const toasts = useToasts();

  const queryClient = useQueryClient();

  const savePostsOrder = useCallback(
    async (
      sortablePosts: Array<
        Omit<EditorialFacebookPostResource, 'id'> & { id: string }
      >,
    ) => {
      const toastId = toasts.create('loading', 'Saving...');

      try {
        const sortedPosts = sortablePosts.map((p, i) => ({
          id: p.id,
          queueOrder: posts[i].queueOrder,
        }));

        await api.patch('/v1/editorial/queue/posts-order', {
          posts: sortedPosts,
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_schedules',
        ]);

        toasts.close(toastId);
      } catch {
        toasts.error(toastId, 'Error');
      }
    },
    [toasts, posts, queryClient],
  );

  const onDragEnd = useCallback(
    (result: any) => {
      if (!result.destination) {
        return;
      }

      setSortablePosts((prev) => {
        const updated = update(prev, {
          $splice: [
            [result.source.index, 1],
            [result.destination.index, 0, prev[result.source.index]],
          ],
        });

        savePostsOrder(updated);

        return updated;
      });
    },
    [savePostsOrder],
  );

  useEffect(() => {
    setSortablePosts(posts.map((p) => ({ ...p, id: p.id.toString() })));
  }, [posts]);

  return (
    <StyledTable $fixed>
      <thead>
        <tr>
          <Th>Order Nr.</Th>
          <Th>Cover</Th>
          <Th>Message</Th>
          {/* <Th>Source Link</Th> */}
          <Th>Content Type</Th>
        </tr>
      </thead>
      <DragDropContext onDragEnd={onDragEnd}>
        <StrictModeDroppable droppableId="table-body">
          {(provided) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              <TableContent
                isLoading={false}
                isError={false}
                hasData={!!posts.length}
                colSpan={4}
              >
                {sortablePosts.map((p, i) => (
                  <SortableTableRow key={p.id.toString()} index={i} post={p} />
                ))}
              </TableContent>
              {provided.placeholder}
            </tbody>
          )}
        </StrictModeDroppable>
      </DragDropContext>
    </StyledTable>
  );
};

export default SortableTable;
